import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, ArrowRight, Loader2, TrendingUp, Zap, Edit,
  Share, Layers, DollarSign, Twitter, ChevronRight, Mic
} from "lucide-react";
import { API, LOGO_URL } from "@/config";

const ICON_MAP = {
  twitter: Twitter,
  trending: TrendingUp,
  edit: Edit,
  dollar: DollarSign,
  sparkles: Sparkles,
  layers: Layers,
  share: Share,
  zap: Zap,
  mic: Mic,
};

const CATEGORY_COLORS = {
  monetization: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  affiliate: "bg-teal-500/10 text-teal-400 border-teal-500/20",
  productivity: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  tools: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  podcast: "bg-pink-500/10 text-pink-400 border-pink-500/20",
};

export default function TemplatesDirectoryPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/seo/templates`);
        setTemplates(res.data);
      } catch (e) {}
      setLoading(false);
    };
    fetch();
  }, []);

  return (
    <>
      <Helmet>
        <title>Free AI Content Templates for Monetization — Creatara</title>
        <meta name="description" content="Browse free AI-powered content templates designed for monetization. Generate Twitter threads, blog posts, affiliate content, and social media posts that drive revenue. By Creatara." />
        <meta property="og:title" content="Free AI Content Templates for Monetization — Creatara" />
        <meta property="og:description" content="Browse free AI-powered content templates designed for monetization. Generate revenue-driving content in seconds." />
        <meta name="keywords" content="content monetization, AI content templates, Twitter monetization 2026, AI affiliate marketing, content creator tools, Creatara" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <nav className="fixed top-0 w-full z-50 glass">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Creatara - Browse Revenue Templates for Content Monetization" className="w-10 h-10 rounded-lg" />
              <span className="text-xl font-bold">Creatara</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-muted-foreground hover:text-foreground text-sm hidden sm:block">Home</Link>
              <Button onClick={() => navigate("/register")} className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-6" data-testid="templates-dir-cta">
                Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </nav>

        <section className="hero-glow pt-28 pb-16 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Badge className="mb-6 bg-violet-600/10 text-violet-500 border-violet-600/20 px-4 py-1">Revenue Templates</Badge>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight gradient-text mb-4">
                AI Templates Built to<br />Monetize Your Content
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Each template is keyword-optimized and designed to generate content that drives revenue. Pick a template, enter your topic, and start earning.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-12 px-6">
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {templates.map((t, i) => {
                  const Icon = ICON_MAP[t.icon] || Sparkles;
                  const catColor = CATEGORY_COLORS[t.category] || "bg-violet-600/10 text-violet-500 border-violet-600/20";
                  return (
                    <motion.div key={t.slug} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
                      <Link to={`/t/${t.slug}`}>
                        <Card className="glass card-hover h-full group" data-testid={`template-card-${t.slug}`}>
                          <CardContent className="p-6">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-xl bg-violet-600/20 flex items-center justify-center shrink-0">
                                <Icon className="w-6 h-6 text-violet-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge className={catColor}>{t.category}</Badge>
                                  <Badge variant="secondary">{t.content_type}</Badge>
                                </div>
                                <h3 className="text-lg font-bold mb-1 group-hover:text-violet-400 transition-colors">{t.name}</h3>
                                <p className="text-sm text-muted-foreground line-clamp-2">{t.description}</p>
                                <div className="flex items-center gap-1 mt-3 text-violet-500 text-sm font-medium">
                                  Use this template <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="py-20 px-6 bg-card/30">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Start Monetizing Your Content Today</h2>
            <p className="text-muted-foreground mb-8">Join creators using Creatara to turn content into revenue streams.</p>
            <Button onClick={() => navigate("/register")} size="lg" className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-8 py-6 text-lg">
              Get Started Free <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>

        <footer className="border-t border-border py-8 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3"><img src={LOGO_URL} alt="Creatara - AI-Powered Content Monetization Platform" className="w-8 h-8 rounded-lg" /><span className="font-semibold">Creatara</span></div>
            <p className="text-sm text-muted-foreground">© 2026 ProJohnson Consulting Ltd. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
